import {
  useChatSessionDocumentProcessing,
  useChatSessionGroupDocumentProcessing
} from '@/composables/useDocumentProcessing'
import type {
  AiChatQuestionTemplate,
  AIChatSession,
  CustomerOrganisationUser,
  SessionGroup,
  TUser
} from '@/types/generated'
import {
  AiChatQuestionTemplatesService,
  AiChatSessionGroupsService,
  AiChatSessionsService,
  AIChatSessionTypeEnum,
  UsersService
} from '@/types/generated'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useGlobalChatStore = defineStore('globalChat', () => {
  // State
  const sessionGroups = ref<SessionGroup[]>([])
  const activeSessionGroup = ref<SessionGroup | null>(null)
  const chatSessions = ref<AIChatSession[]>([])
  const activeChatSession = ref<AIChatSession | null>(null)
  const promptTemplates = ref<AiChatQuestionTemplate[]>([])
  const organizationUsers = ref<TUser[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Document processors for global chat - each session gets its own processor
  const sessionProcessors = ref<Map<string, any>>(new Map())
  const folderProcessors = ref<Map<string, any>>(new Map())

  // Get or create processor for a specific session
  const getSessionProcessor = (sessionUuid: string) => {
    if (!sessionProcessors.value.has(sessionUuid)) {
      const sessionRef = computed(
        () => chatSessions.value.find((s) => s.uuid === sessionUuid) || null
      )
      const processor = useChatSessionDocumentProcessing(sessionRef)
      sessionProcessors.value.set(sessionUuid, processor)
    }
    return sessionProcessors.value.get(sessionUuid)
  }

  // Get or create processor for a specific folder
  const getFolderProcessor = (folderUuid: string) => {
    if (!folderProcessors.value.has(folderUuid)) {
      const groupRef = computed(
        () => sessionGroups.value.find((f) => f.uuid === folderUuid) || null
      )
      const processor = useChatSessionGroupDocumentProcessing(groupRef)
      folderProcessors.value.set(folderUuid, processor)
    }
    return folderProcessors.value.get(folderUuid)
  }

  // Legacy processors for backward compatibility
  const chatSessionProcessor = computed(() => {
    const session = activeChatSession.value
    return session?.uuid ? getSessionProcessor(session.uuid) : null
  })

  const folderProcessor = computed(() => {
    const group = activeSessionGroup.value
    return group?.uuid ? getFolderProcessor(group.uuid) : null
  })

  // Getters
  const hasSessionGroups = computed(() => sessionGroups.value.length > 0)
  const hasChatSessions = computed(() => chatSessions.value.length > 0)

  // Actions
  const loadSessionGroups = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await AiChatSessionGroupsService.aiChatSessionGroupsList()
      sessionGroups.value = response.results
    } catch (err) {
      error.value = 'Failed to load session groups'
      console.error('Error loading session groups:', err)
    } finally {
      loading.value = false
    }
  }

  const loadStandaloneGlobalChatSessions = async () => {
    try {
      loading.value = true
      error.value = null
      // Load global chat sessions
      const response = await AiChatSessionsService.aiChatSessionsList({
        sessionType: AIChatSessionTypeEnum.GLOBAL
      })
      chatSessions.value = response.results
    } catch (err) {
      error.value = 'Failed to load global chat sessions'
      console.error('Error loading global chat sessions:', err)
    } finally {
      loading.value = false
    }
  }

  const loadGlobalPromptTemplates = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesList({
        limit: 0
      })
      promptTemplates.value = response.results
    } catch (err) {
      error.value = 'Failed to load global prompt templates'
      console.error('Error loading global prompt templates:', err)
    } finally {
      loading.value = false
    }
  }

  const loadOrganizationUsers = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await UsersService.usersList({
        isActive: true,
        limit: 100 // Get all users in the organization
      })
      organizationUsers.value = response.results
    } catch (err) {
      error.value = 'Failed to load organization users'
      console.error('Error loading organization users:', err)
    } finally {
      loading.value = false
    }
  }

  const loadSessionGroup = async (uuid: string) => {
    try {
      loading.value = true
      error.value = null
      const sessionGroup = await AiChatSessionGroupsService.aiChatSessionGroupsRetrieve({ uuid })
      activeSessionGroup.value = sessionGroup

      // Load chat sessions for this group
      await loadChatSessions(uuid)
    } catch (err) {
      error.value = 'Failed to load session group'
      console.error('Error loading session group:', err)
    } finally {
      loading.value = false
    }
  }

  const loadChatSessions = async (sessionGroupUuid: string) => {
    try {
      loading.value = true
      error.value = null
      // Load global chat sessions filtered by session group on backend
      const response = await AiChatSessionsService.aiChatSessionsList({
        sessionType: AIChatSessionTypeEnum.GLOBAL,
        sessionGroup: sessionGroupUuid
      })
      chatSessions.value = response.results
    } catch (err) {
      error.value = 'Failed to load chat sessions for session group'
      console.error('Error loading chat sessions for session group:', err)
    } finally {
      loading.value = false
    }
  }

  const setActiveSessionGroupFromChatSession = async (chatSession: AIChatSession) => {
    if (!chatSession.session_group) {
      activeSessionGroup.value = null
      return
    }

    // Check if we already have the session group loaded
    const existingGroup = sessionGroups.value.find(
      (group) => group.uuid === chatSession.session_group
    )
    if (existingGroup) {
      activeSessionGroup.value = existingGroup
      return
    }

    // Load the session group if we don't have it
    try {
      const sessionGroup = await AiChatSessionGroupsService.aiChatSessionGroupsRetrieve({
        uuid: chatSession.session_group
      })
      activeSessionGroup.value = sessionGroup
    } catch (err) {
      console.error('Failed to load session group for chat session:', err)
      activeSessionGroup.value = null
    }
  }

  const createChatSession = async (
    sessionGroupUuid: string,
    title?: string,
    description?: string
  ) => {
    try {
      loading.value = true
      error.value = null

      const session = await AiChatSessionsService.aiChatSessionsCreate({
        requestBody: {
          session_group_uuid: sessionGroupUuid,
          title: title || 'New Chat',
          description: description || '',
          session_type: AIChatSessionTypeEnum.GLOBAL
        }
      })

      chatSessions.value.unshift(session)
      activeChatSession.value = session

      return session
    } catch (err) {
      error.value = 'Failed to create chat session'
      console.error('Error creating chat session:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const setActiveChatSession = (session: AIChatSession | null) => {
    activeChatSession.value = session
  }

  const updateChatSession = (updatedSession: AIChatSession) => {
    const index = chatSessions.value.findIndex((s) => s.uuid === updatedSession.uuid)
    if (index !== -1) {
      chatSessions.value[index] = updatedSession
    }

    if (activeChatSession.value?.uuid === updatedSession.uuid) {
      activeChatSession.value = updatedSession
    }
  }

  const clearError = () => {
    error.value = null
  }

  const getUserByUuid = (uuid: string) => {
    // First try to find user data from chat sessions (more reliable)
    for (const session of chatSessions.value) {
      const userData = (session as any).started_by_user_data
      if (userData && userData.uuid === uuid) {
        return userData
      }
    }

    // Fallback to organization users
    const user = organizationUsers.value.find((user) => user.uuid === uuid) || null
    return user
  }

  const getOrganisationUserByUuid = (uuid: string): CustomerOrganisationUser | null => {
    // First try to find user data from chat sessions (more reliable)
    for (const session of chatSessions.value) {
      const userData = (session as any).started_by_user_data
      if (userData && userData.uuid === uuid) {
        // Map to CustomerOrganisationUser format
        return {
          uuid: userData.uuid,
          email: userData.email,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          avatar: userData.avatar || null
        }
      }
    }

    // Fallback to organization users
    const user = organizationUsers.value.find((user) => user.uuid === uuid)
    if (user && user.email && user.uuid) {
      return {
        uuid: user.uuid,
        email: user.email,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        avatar: user.avatar || null
      }
    }

    return null
  }

  // Session Group Management
  const createSessionGroup = async (data: {
    name: string
    description?: string
    instructions?: string
  }) => {
    try {
      loading.value = true
      error.value = null
      const response = await AiChatSessionGroupsService.aiChatSessionGroupsCreate({
        requestBody: {
          name: data.name,
          description: data.description,
          instructions: data.instructions
        }
      })
      sessionGroups.value.unshift(response)
      return response
    } catch (err) {
      error.value = 'Failed to create session group'
      console.error('Error creating session group:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateSessionGroup = async (
    uuid: string,
    data: { name: string; description?: string; instructions?: string }
  ) => {
    try {
      loading.value = true
      error.value = null
      const response = await AiChatSessionGroupsService.aiChatSessionGroupsUpdate({
        uuid,
        requestBody: {
          name: data.name,
          description: data.description,
          instructions: data.instructions
        }
      })
      const index = sessionGroups.value.findIndex((group) => group.uuid === uuid)
      if (index !== -1) {
        sessionGroups.value[index] = response
      }
      return response
    } catch (err) {
      error.value = 'Failed to update session group'
      console.error('Error updating session group:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const refreshSessionGroup = async (uuid: string) => {
    try {
      loading.value = true
      error.value = null
      const response = await AiChatSessionGroupsService.aiChatSessionGroupsRetrieve({ uuid })
      const index = sessionGroups.value.findIndex((group) => group.uuid === uuid)
      if (index !== -1) {
        sessionGroups.value[index] = response
      }
      return response
    } catch (err) {
      error.value = 'Failed to refresh session group'
      console.error('Error refreshing session group:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteSessionGroup = async (uuid: string) => {
    try {
      loading.value = true
      error.value = null

      // Get chat sessions that belong to this folder before deletion
      const sessionsToDelete = chatSessions.value.filter(
        (session) => session.session_group === uuid
      )

      await AiChatSessionGroupsService.aiChatSessionGroupsDestroy({ uuid })

      // Remove the session group from the store
      sessionGroups.value = sessionGroups.value.filter((group) => group.uuid !== uuid)

      // Remove all chat sessions that belonged to this folder
      chatSessions.value = chatSessions.value.filter((session) => session.session_group !== uuid)

      // Clean up processors for all deleted sessions
      sessionsToDelete.forEach((session) => {
        if (sessionProcessors.value.has(session.uuid)) {
          sessionProcessors.value.delete(session.uuid)
        }
      })

      // Clean up the folder processor
      if (folderProcessors.value.has(uuid)) {
        folderProcessors.value.delete(uuid)
      }

      // If the active chat session was in this folder, clear it
      if (activeChatSession.value?.session_group === uuid) {
        activeChatSession.value = null
      }

      // If this was the active session group, clear it
      if (activeSessionGroup.value?.uuid === uuid) {
        activeSessionGroup.value = null
      }
    } catch (err) {
      error.value = 'Failed to delete session group'
      console.error('Error deleting session group:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteChatSession = async (uuid: string) => {
    try {
      loading.value = true
      error.value = null
      await AiChatSessionsService.aiChatSessionsDestroy({ uuid })
      chatSessions.value = chatSessions.value.filter((session) => session.uuid !== uuid)
      // Clean up the session processor
      if (sessionProcessors.value.has(uuid)) {
        sessionProcessors.value.delete(uuid)
      }
      // If this was the active chat session, clear it
      if (activeChatSession.value?.uuid === uuid) {
        activeChatSession.value = null
      }
    } catch (err) {
      error.value = 'Failed to delete chat session'
      console.error('Error deleting chat session:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    sessionGroups,
    activeSessionGroup,
    chatSessions,
    activeChatSession,
    promptTemplates,
    organizationUsers,
    loading,
    error,

    // Getters
    hasSessionGroups,
    hasChatSessions,

    // Document Processors
    chatSessionProcessor,
    folderProcessor,
    getSessionProcessor,
    getFolderProcessor,

    // Actions
    loadSessionGroups,
    loadStandaloneGlobalChatSessions,
    loadGlobalPromptTemplates,
    loadOrganizationUsers,
    loadSessionGroup,
    loadChatSessions,
    setActiveSessionGroupFromChatSession,
    createChatSession,
    setActiveChatSession,
    updateChatSession,
    clearError,
    getUserByUuid,
    getOrganisationUserByUuid,

    // Session Group Management
    createSessionGroup,
    updateSessionGroup,
    refreshSessionGroup,
    deleteSessionGroup,

    // Chat Session Management
    deleteChatSession
  }
})
